import React, { useEffect, useRef } from "react";

/**
 * Live preview of the clip the server will render.
 *
 * The layout is not computed here. The server sends the exact primitive list
 * it will rasterise, and this component only paints it, so the preview and the
 * final MP4 cannot drift apart.
 */

const DESIGN_WIDTH = 720;
const DESIGN_HEIGHT = 1280;

function drawRoundedRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

function drawText(ctx, p) {
  ctx.save();
  ctx.font = `${p.fontWeight || 400} ${p.fontSize}px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif`;
  ctx.fillStyle = p.fill;
  ctx.textAlign = p.anchor === "end" ? "right" : "left";
  ctx.textBaseline = "alphabetic";

  if (p.letterSpacing && typeof ctx.letterSpacing === "string") {
    ctx.letterSpacing = `${p.letterSpacing}px`;
  }
  ctx.fillText(p.text, p.x, p.y);
  ctx.restore();
}

/** Images arrive as URLs, so they resolve after the first synchronous paint. */
function useImageCache(primitives) {
  const cacheRef = useRef(new Map());

  useEffect(() => {
    const cache = cacheRef.current;
    for (const p of primitives || []) {
      if (p.type !== "image" || !p.href || cache.has(p.href)) continue;
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = p.href;
      cache.set(p.href, img);
    }
  }, [primitives]);

  return cacheRef;
}

export default function ViralClipOverlay({
  layout,
  className = "",
  style = {},
  onReady = null,
}) {
  const canvasRef = useRef(null);
  const primitives = layout?.primitives || [];
  const imageCache = useImageCache(primitives);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !primitives.length) return undefined;

    let cancelled = false;

    const paint = () => {
      if (cancelled) return;
      const ctx = canvas.getContext("2d");
      const width = layout?.width || DESIGN_WIDTH;
      const height = layout?.height || DESIGN_HEIGHT;

      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);

      for (const p of primitives) {
        switch (p.type) {
          case "linearGradient": {
            const grad = ctx.createLinearGradient(p.x, p.y, p.x, p.y + p.height);
            grad.addColorStop(0, p.from);
            grad.addColorStop(1, p.to);
            ctx.fillStyle = grad;
            ctx.fillRect(p.x, p.y, p.width, p.height);
            break;
          }
          case "rect":
            ctx.fillStyle = p.fill;
            if (p.radius) drawRoundedRect(ctx, p.x, p.y, p.width, p.height, p.radius);
            else ctx.fillRect(p.x, p.y, p.width, p.height);
            break;

          case "roundedRect":
            ctx.fillStyle = p.fill;
            drawRoundedRect(ctx, p.x, p.y, p.width, p.height, p.radius);
            break;

          case "text":
            drawText(ctx, p);
            break;

          case "image": {
            const img = imageCache.current.get(p.href);
            if (img?.complete && img.naturalWidth > 0) {
              ctx.drawImage(img, p.x, p.y, p.width, p.height);
            }
            break;
          }
          default:
            break;
        }
      }

      onReady?.(canvas);
    };

    paint();

    // Repaint as logos finish loading, rather than leaving gaps in the preview.
    const pending = primitives
      .filter((p) => p.type === "image" && p.href)
      .map((p) => imageCache.current.get(p.href))
      .filter((img) => img && !img.complete);

    const handlers = pending.map((img) => {
      const onLoad = () => paint();
      img.addEventListener("load", onLoad);
      img.addEventListener("error", onLoad);
      return { img, onLoad };
    });

    return () => {
      cancelled = true;
      handlers.forEach(({ img, onLoad }) => {
        img.removeEventListener("load", onLoad);
        img.removeEventListener("error", onLoad);
      });
    };
  }, [layout, primitives, imageCache, onReady]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        ...style,
      }}
      aria-hidden="true"
    />
  );
}
